"use client";

import { useEffect, useState } from "react";
import { PARTNER_CLICK_PROOF_PARAM } from "@/lib/monetization/partner-click-proof-param";

/**
 * Client half of the CPC browser-proof token. Mints a short-TTL, single-job
 * token from `/api/apply/click-proof` after hydration and hands it back so the
 * Apply control can carry it into `/out/[jobId]?bp=…`.
 *
 * The token's whole value is that this code ran. A crawler that walks hrefs
 * without executing our page never calls this, so its click reaches `/out`
 * with no `bp` and is refused before it can bill a partner. See
 * src/lib/monetization/partner-click-proof.ts for the audit that made this
 * necessary.
 *
 * Fetch discipline:
 *  - `credentials: "same-origin"` so the identity cookies the token binds to
 *    actually reach the mint route.
 *  - `cache: "no-store"` — a cached token would be one visitor's identity
 *    handed to another. Locked from the server side by
 *    `pnpm check:no-store-routes`.
 *  - Deduped across mounts. The job-detail page renders TWO `JobApplyCta`
 *    instances (desktop sidebar + mobile fixed footer, one hidden by CSS);
 *    without the in-flight map they would mint twice per view for zero benefit.
 *  - Re-minted when the tab becomes visible again and the token is over half
 *    its life. A seeker who reads a posting for fifteen minutes must not tap
 *    Apply and get bounced for an expired token.
 *
 * Never throws and never blocks render: on any failure the hook returns null
 * and the Apply link stays exactly what it was. The click is then refused
 * server-side, which is the correct degradation — a partner is never billed on
 * a click we cannot stand behind.
 */

type CachedProof = { token: string; mintedAt: number; expiresInMs: number };

/** Shared across component instances on one page — see "Deduped across mounts". */
const proofCache = new Map<string, CachedProof>();
const inFlight = new Map<string, Promise<CachedProof | null>>();

/** Re-mint once the token is past this share of its lifetime. */
const REFRESH_AT_FRACTION = 0.5;

/**
 * How often to re-check freshness on an open tab. Comfortably under
 * `PARTNER_CLICK_PROOF_TTL_MS * REFRESH_AT_FRACTION` so a token is replaced
 * well before it expires, even on a page that never fires a visibility event.
 */
const REFRESH_INTERVAL_MS = 60 * 1000;

/** Used only if the mint route ever omits `expiresInMs`; mirrors
 *  PARTNER_CLICK_PROOF_TTL_MS, which lives in the server-only module. */
const FALLBACK_TTL_MS = 10 * 60 * 1000;

type MintResponse = { ok: true; token: string; expiresInMs?: unknown };

function isMintResponse(body: unknown): body is MintResponse {
	if (typeof body !== "object" || body === null) return false;
	const candidate = body as Record<string, unknown>;
	return (
		candidate.ok === true &&
		typeof candidate.token === "string" &&
		candidate.token.length > 0
	);
}

function isFresh(proof: CachedProof, now: number): boolean {
	return now - proof.mintedAt < proof.expiresInMs * REFRESH_AT_FRACTION;
}

async function fetchProof(jobId: string): Promise<CachedProof | null> {
	const existing = inFlight.get(jobId);
	if (existing) return existing;

	const request = (async (): Promise<CachedProof | null> => {
		try {
			const res = await fetch(
				`/api/apply/click-proof?jobId=${encodeURIComponent(jobId)}`,
				{ cache: "no-store", credentials: "same-origin" },
			);
			if (!res.ok) return null;
			// Narrowed, never cast: this is an HTTP response, so its shape is a
			// runtime question. A `as {token: string}` here would make a
			// mint-route regression surface as `undefined` on the URL instead of
			// as a null token (`pnpm check:architecture-ratchet` → json_as_t_casts).
			const body: unknown = await res.json();
			if (!isMintResponse(body)) return null;
			const proof: CachedProof = {
				token: body.token,
				mintedAt: Date.now(),
				expiresInMs:
					typeof body.expiresInMs === "number" && body.expiresInMs > 0
						? body.expiresInMs
						: FALLBACK_TTL_MS,
			};
			proofCache.set(jobId, proof);
			return proof;
		} catch {
			// Offline, blocked, aborted mid-navigation — never surface to the user.
			return null;
		} finally {
			inFlight.delete(jobId);
		}
	})();

	inFlight.set(jobId, request);
	return request;
}

/**
 * @param jobId  The job this token may pay for.
 * @param enabled  Every job that applies through `/out` mints (2026-09-06);
 *   only a mailto CTA opts out, having no hop to prove. This used to be
 *   passthrough-only on the grounds that a non-billable click bought nothing —
 *   that stopped being true when the guest-apply cap started reading the token
 *   as its person-vs-crawler test, so a non-minting job is one whose cap a
 *   swarm can exhaust. Measured cost at the time of widening: ~319 job
 *   views/day, ~9.6k mints/month, one HMAC each, no D1 and no KV.
 * @returns the current token, or null when none could be minted.
 */
export function usePartnerClickProof(
	jobId: string,
	enabled: boolean,
): string | null {
	const [token, setToken] = useState<string | null>(null);

	useEffect(() => {
		if (!enabled || !jobId) {
			setToken(null);
			return;
		}
		let active = true;

		const ensure = () => {
			const cached = proofCache.get(jobId);
			if (cached && isFresh(cached, Date.now())) {
				// Synchronous path: `ensure` only runs from the effect body, the
				// visibility listener or the interval — all torn down in the same
				// tick that sets `active = false` — so `active` is always true here.
				setToken(cached.token);
				return;
			}
			void fetchProof(jobId).then((proof) => {
				if (active) setToken(proof?.token ?? null);
			});
		};

		ensure();

		const onVisible = () => {
			if (document.visibilityState === "visible") ensure();
		};
		document.addEventListener("visibilitychange", onVisible);

		// A TIMER, not just `visibilitychange`. A seeker who reads a long job
		// description for twelve minutes straight never fires a visibility
		// event, so a visibility-only refresh left exactly the most engaged
		// reader holding an expired token — refused at the moment they finally
		// clicked Apply, and bounced back to the page they were already on.
		// `ensure()` is a no-op while the cached token is still fresh, so this
		// costs one mint per half-TTL on a VISIBLE tab and nothing otherwise.
		//
		// Skipped while hidden on purpose: a backgrounded job page would
		// otherwise mint every half-TTL forever for a click that is not coming,
		// and nobody can click Apply on a tab they cannot see. The
		// `visibilitychange` handler above re-mints the moment they come back,
		// so this costs the returning reader nothing.
		const refresh = setInterval(() => {
			if (document.visibilityState === "visible") ensure();
		}, REFRESH_INTERVAL_MS);

		return () => {
			active = false;
			clearInterval(refresh);
			document.removeEventListener("visibilitychange", onVisible);
		};
	}, [jobId, enabled]);

	return token;
}

/** Append a minted token to an apply URL, leaving it untouched when there is none. */
export function withPartnerClickProof(
	applyUrl: string,
	token: string | null,
): string {
	if (!token) return applyUrl;
	return `${applyUrl}${applyUrl.includes("?") ? "&" : "?"}${PARTNER_CLICK_PROOF_PARAM}=${encodeURIComponent(token)}`;
}
